import type { KCiEvent, KCiEventPayload, PublishOptions } from './types';

type Handler<T = KCiEventPayload> = (event: KCiEvent<T>) => void;

export class KCiEventBus {
  private handlers: Map<string, Set<Handler>> = new Map();
  private history: KCiEvent[] = [];
  private readonly HISTORY_LIMIT = 50;

  private generateCorrelationId(): string {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
      return globalThis.crypto.randomUUID();
    }

    return `cid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  publish<T extends KCiEventPayload = KCiEventPayload>(
    type: string,
    payload: T,
    options: PublishOptions,
  ): void {
    const event: KCiEvent<T> = {
      type,
      payload,
      source: options.source,
      target: options.target,
      timestamp: Date.now(),
      correlationId: this.generateCorrelationId(),
    };

    this.recordHistory(event);

    const subscribers = this.handlers.get(type);
    if (!subscribers || subscribers.size === 0) {
      return;
    }

    [...subscribers].forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.error(`[KCiEventBus] Handler error for "${type}":`, error);
      }
    });
  }

  subscribe<T extends KCiEventPayload = KCiEventPayload>(
    type: string,
    handler: Handler<T>,
  ): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }

    this.handlers.get(type)?.add(handler as Handler);

    return () => this.unsubscribe(type, handler as Handler);
  }

  unsubscribe(type: string, handler: Handler): void {
    this.handlers.get(type)?.delete(handler);
  }

  getLatest<T extends KCiEventPayload = KCiEventPayload>(
    type: string,
  ): KCiEvent<T> | undefined {
    const matches = this.history.filter((entry) => entry.type === type);
    return matches[matches.length - 1] as KCiEvent<T> | undefined;
  }

  getHistory(type?: string): KCiEvent[] {
    return type
      ? this.history.filter((entry) => entry.type === type)
      : [...this.history];
  }

  private recordHistory(event: KCiEvent): void {
    this.history.push(event);

    if (this.history.length > this.HISTORY_LIMIT) {
      this.history.shift();
    }
  }
}

declare global {
  interface Window {
    __KCI_EVENT_BUS__?: KCiEventBus;
  }
}

if (!window.__KCI_EVENT_BUS__) {
  window.__KCI_EVENT_BUS__ = new KCiEventBus();
}

export const kciEventBus: KCiEventBus = window.__KCI_EVENT_BUS__ as KCiEventBus;
